import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import { faTag } from "@fortawesome/pro-solid-svg-icons";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { faPaperclip } from "@fortawesome/pro-solid-svg-icons/faPaperclip";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { isEqual } from "lodash-es";
import { useIntl } from "react-intl";

import FileTag from "@mapmycustomers/shared/types/entity/FileTag";
import File, { RawFile } from "@mapmycustomers/shared/types/File";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, LoadingSpinner, Popconfirm } from "@mapmycustomers/ui";

import { updateFile } from "@app/store/files/actions";

import FileTags from "../FileTags";
import FileTagSelector from "../FileTagSelector";

import styles from "./FileItem.module.scss";
import messages from "./messages";

type FileItemProps = {
  disabled?: boolean;
  errored?: boolean;
  file?: RawFile;
  fileClassName?: string;
  fileName?: string;
  onChange?: (file: RawFile) => void;
  onClick?: (file: RawFile) => void;
  onRemove?: () => void;
  removeTooltip?: string;
  supportsTags?: boolean;
  uploading?: boolean;
} & typeof mapDispatchToProps;

const FileItem: React.FC<FileItemProps> = ({
  disabled,
  errored,
  file,
  fileClassName,
  fileName,
  onChange,
  onClick,
  onRemove,
  onUpdateFile,
  removeTooltip,
  supportsTags,
  uploading,
}) => {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);

  const [tags, setTags] = useState<File["tags"]>(file?.tags);
  const [editingTags, startEditing, stopEditing] = useBoolean();

  const handleFileTagsChange = useCallback(
    (tags: File["tags"]) => {
      if (file) {
        setTags(tags);
      }
    },
    [file]
  );

  const handleClickOutside = useCallback(
    (event) => {
      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        !event.target.closest(".ant-dropdown")
      ) {
        if (file && editingTags) {
          stopEditing();
          if (!isEqual(tags, file.tags) && (tags?.length || file.tags?.length)) {
            onUpdateFile({
              callback: (updatedFile) => {
                onChange?.(updatedFile);
              },
              file: { id: file.id, name: file.name, tagIds: (tags ?? []).map(({ id }) => id) },
            });
          }
        } else {
          stopEditing();
        }
      }
    },
    [file, editingTags, stopEditing, onUpdateFile, tags, onChange]
  );

  const handleRemoveTag = useCallback((tagId: FileTag["id"]) => {
    setTags((tags) => tags?.filter(({ id }) => id !== tagId));
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    setTags(file?.tags);
  }, [file]);

  return (
    <div className={styles.file} ref={ref}>
      <div className={cn(styles.fileRow, fileClassName, { [styles.allowTag]: !errored })}>
        <FontAwesomeIcon icon={errored ? faExclamationCircle : faPaperclip} />
        <div className={styles.fileName}>
          <div className={styles.fileRowLabel}>
            <span className={styles.name} onClick={() => file && onClick?.(file)}>
              {fileName ?? intl.formatMessage(messages.emptyFileName)}
            </span>
            {supportsTags &&
              !uploading &&
              !errored &&
              (editingTags ? (
                <FileTagSelector
                  className={styles.fileTagSelector}
                  onChange={handleFileTagsChange}
                  value={tags}
                />
              ) : (
                <div className={styles.addTagContainer}>
                  <FontAwesomeIcon icon={faTag} onClick={startEditing} />
                </div>
              ))}
          </div>
        </div>
        {onRemove && uploading ? (
          <LoadingSpinner mini />
        ) : (
          !errored &&
          (!disabled || removeTooltip) && (
            <Popconfirm
              cancelText={intl.formatMessage(messages.deletePopConfirmCancelText)}
              description={intl.formatMessage(messages.deletePopConfirmTitle)}
              disabled={disabled}
              okText={intl.formatMessage(messages.deletePopConfirmOkText)}
              onConfirm={onRemove}
            >
              <Tooltip title={removeTooltip ?? null}>
                <Button
                  danger
                  disabled={disabled}
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  size="small"
                  type="link"
                />
              </Tooltip>
            </Popconfirm>
          )
        )}
      </div>
      {tags && tags.length > 0 && (
        <div className={styles.tagsRow}>
          <FileTags
            className={styles.tags}
            onRemoveTag={editingTags ? handleRemoveTag : undefined}
            tags={tags}
          />
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = {
  onUpdateFile: updateFile.request,
};

export default connect(null, mapDispatchToProps)(FileItem);
